/* eslint-disable no-restricted-syntax -- The admin's own design system (shared/rd), ported from the Restaurant Dashboard design; admin chose app-local components over @a2z/ui-v2 (2026-10-26). See apps/admin/AGENTS.md. */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ChevronsRight } from "lucide-react";
import { Badge } from "./icons";
import { cx } from "./cx";
import { Button } from "./controls";

/** A white panel with the hairline ring: the same frame as `DataTable`. */
export function Card({
  padding = 20,
  className,
  style,
  children,
  ...rest
}: {
  padding?: number | string;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
} & Omit<React.HTMLAttributes<HTMLElement>, "style">) {
  return (
    <section className={cx("rd-card", className)} style={{ padding, ...style }} {...rest}>
      {children}
    </section>
  );
}

/** A dashboard tile that answers one question in a sentence. */
export function AnswerTile({
  badge,
  label,
  answer,
  sub,
  tone,
  onClick,
  className,
}: {
  badge?: string;
  label: ReactNode;
  answer: ReactNode;
  sub?: ReactNode;
  /** good = green, bad = red, warn = amber sub-line. */
  tone?: "good" | "bad" | "warn";
  onClick?: () => void;
  className?: string;
}) {
  const body = (
    <>
      {badge ? <Badge id={badge} size={36} /> : null}
      <span className="rd-tile-body">
        <span className="rd-tile-label">{label}</span>
        <span className="rd-tile-answer">{answer}</span>
        {sub ? <span className={cx("rd-tile-sub", tone && `rd-tone-${tone}`)}>{sub}</span> : null}
      </span>
    </>
  );
  return onClick ? (
    <button type="button" className={cx("rd-tile", className)} onClick={onClick}>
      {body}
    </button>
  ) : (
    <div className={cx("rd-tile", className)}>{body}</div>
  );
}

/** Auto-fit tile grid. `min` is the narrowest column (Today 200, Invoices 160). */
export function TileGrid({ min, className, style, children }: { min?: number; className?: string; style?: CSSProperties; children: ReactNode }) {
  return (
    <div
      className={cx("rd-tiles", className)}
      style={{
        ...(min ? { gridTemplateColumns: `repeat(auto-fit,minmax(min(100%,${min}px),1fr))` } : null),
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** A figure with a short label ("214 · Sellers"). */
export function StatTile({
  badge,
  value,
  label,
  onClick,
  className,
}: {
  badge?: string;
  value: ReactNode;
  label: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  const body = (
    <>
      {badge ? <Badge id={badge} size={40} /> : null}
      <span className="rd-stat-body">
        <span className="rd-stat-value">{value}</span>
        <span className="rd-stat-label">{label}</span>
      </span>
    </>
  );
  return onClick ? (
    <button type="button" className={cx("rd-stat", className)} onClick={onClick}>
      {body}
    </button>
  ) : (
    <div className={cx("rd-stat", className)}>{body}</div>
  );
}

/** Says what is missing and the one thing to do next. */
export function EmptyState({ badge, action, className, children }: { badge?: string; action?: ReactNode; className?: string; children: ReactNode }) {
  return (
    <div className={cx("rd-empty", className)}>
      {badge ? <Badge id={badge} size={40} /> : null}
      <span>{children}</span>
      {action ?? null}
    </div>
  );
}

function useEscape(open: boolean, onClose?: () => void) {
  useEffect(() => {
    if (!open || !onClose) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
}

/** Only for a short decision: one question, at most one input, two buttons. */
export function Modal({
  open,
  onClose,
  title,
  actions,
  width = 480,
  className,
  children,
}: {
  open: boolean;
  onClose?: () => void;
  title?: ReactNode;
  actions?: ReactNode;
  width?: number;
  className?: string;
  children?: ReactNode;
}) {
  useEscape(open, onClose);
  if (!open) return null;
  return createPortal(
    <div className="rd-root rd-overlay" onClick={onClose}>
      <div
        className={cx("rd-modal", className)}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        style={{ maxWidth: width }}
        onClick={(event) => event.stopPropagation()}
      >
        {title ? <div className="rd-modal-title">{title}</div> : null}
        {children}
        {actions ? <div className="rd-modal-actions">{actions}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

/** The drawer's slide, in ms (components.css `.rd-drawer` transition). */
const DRAWER_MS = 320;

/** Creating, editing and a record's details. Slides in from the inline end. */
export function Drawer({
  open,
  onClose,
  closeLabel,
  crumb,
  badge,
  title,
  sub,
  actions,
  width = 500,
  dismissible = true,
  className,
  children,
}: {
  open: boolean;
  onClose?: () => void;
  /** Accessible name of the close button. */
  closeLabel: string;
  /**
   * False: Escape and a click outside do nothing; only the close button
   * closes. For a drawer holding something shown once (a temporary password).
   */
  dismissible?: boolean;
  crumb?: ReactNode;
  badge?: string;
  title?: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  /** 460–580; default 500. */
  width?: number;
  className?: string;
  children?: ReactNode;
}) {
  // Closing plays the slide-out first, THEN tells the owner. An owner that
  // closes by navigating (/stores/:id → /stores) unmounts the drawer at once,
  // so without this the drawer vanished instead of sliding away.
  const [leaving, setLeaving] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const requestClose = () => {
    if (!onClose || leaving) return;
    const reduced = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      onClose();
      return;
    }
    setLeaving(true);
    timer.current = window.setTimeout(() => {
      setLeaving(false);
      onClose();
    }, DRAWER_MS);
  };
  const shown = open && !leaving;
  useEscape(shown, dismissible ? requestClose : undefined);
  const dir = typeof document !== "undefined" ? document.documentElement.dir || undefined : undefined;
  return createPortal(
    <div className="rd-root" dir={dir} style={{ background: "transparent" }}>
      <div className={cx("rd-drawer-scrim", shown && "rd-open")} onClick={dismissible ? requestClose : undefined} />
      <aside
        className={cx("rd-drawer", shown && "rd-open", className)}
        role="dialog"
        aria-modal="true"
        aria-hidden={!shown}
        aria-label={typeof title === "string" ? title : undefined}
        style={{ width }}
      >
        <div className="rd-drawer-bar">
          <Button variant="ghost" iconOnly icon={ChevronsRight} title={closeLabel} onClick={requestClose} className="rd-flip-rtl" />
          {crumb ? <span className="rd-drawer-crumb">{crumb}</span> : null}
          <div className="rd-spacer" />
          {actions ?? null}
        </div>
        {title || badge ? (
          <div className="rd-drawer-head">
            {badge ? <Badge id={badge} size={36} /> : null}
            <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
              <span className="rd-drawer-title">{title}</span>
              {sub ? <span className="rd-drawer-sub">{sub}</span> : null}
            </div>
          </div>
        ) : null}
        <div className="rd-drawer-body">{open ? children : null}</div>
      </aside>
    </div>,
    document.body,
  );
}

/** Key–value list for a record's details (`<dl class="rd-kv">`). */
export function KeyValues({ rows }: { rows: Array<{ label: ReactNode; value: ReactNode }> }) {
  return (
    <dl className="rd-kv">
      {rows.map((row, index) => (
        <div key={index} style={{ display: "contents" }}>
          <dt>{row.label}</dt>
          <dd>{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}
